import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { BrandLoading } from "./components/BrandStates";
// Public / critical surfaces stay eager for instant first paint.
import Landing from "./pages/Landing";
import { Login, Register } from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";
import { Toaster } from "./components/ui/sonner";

// Authenticated app surfaces are code-split; the branded Suspense fallback
// shows while their chunks load, and chunk-load failures are caught by the
// app-level ErrorBoundary.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CurriculumBrowser = lazy(() => import("./pages/CurriculumBrowser"));
const CurriculumAdmin = lazy(() => import("./pages/CurriculumAdmin"));
const KnowledgeAdmin = lazy(() => import("./pages/KnowledgeAdmin"));
const KnowledgeState = lazy(() => import("./pages/KnowledgeState"));
const Diagnosis = lazy(() => import("./pages/Diagnosis"));
const ReferralInbox = lazy(() => import("./pages/ReferralInbox"));
const Recovery = lazy(() => import("./pages/Recovery"));
const TeachingSession = lazy(() => import("./pages/TeachingSession"));
const TeacherTeaching = lazy(() => import("./pages/TeacherTeaching"));
const MistakeIntelligence = lazy(() => import("./pages/MistakeIntelligence"));
const MasteryTeacher = lazy(() => import("./pages/MasteryTeacher"));
const StudentProgress = lazy(() => import("./pages/StudentProgress"));
const StudentJourney = lazy(() => import("./pages/StudentJourney"));
const TeacherJourney = lazy(() => import("./pages/TeacherJourney"));
const RecoveryCommandCenter = lazy(() => import("./pages/RecoveryCommandCenter"));
const TeacherCase = lazy(() => import("./pages/TeacherCase"));
const StudentCase = lazy(() => import("./pages/StudentCase"));
const ClassroomOverview = lazy(() => import("./pages/ClassroomOverview"));
const MyRechecks = lazy(() => import("./pages/MyRechecks"));
const MyLanguage = lazy(() => import("./pages/MyLanguage"));
const LanguageEvidence = lazy(() => import("./pages/LanguageEvidence"));
const AiProposalReview = lazy(() => import("./pages/AiProposalReview"));
const AuthorityReview = lazy(() => import("./pages/AuthorityReview"));
const PilotProvisioning = lazy(() => import("./pages/PilotProvisioning"));
const StudentDataLifecycle = lazy(() => import("./pages/StudentDataLifecycle"));
const BackupRecovery = lazy(() => import("./pages/BackupRecovery"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));

// Dev-only deliberate render-failure probe used to prove the error boundary
// catches and recovers. Guarded by NODE_ENV so it is stripped from production
// builds and never reachable on the live public surface.
function CrashProbe() {
  throw new Error("Deliberate render failure (dev probe)");
}

function AppRouter() {
  const location = useLocation();
  // Detect the Emergent OAuth callback synchronously during render (before
  // ProtectedRoute / auth checks) to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<BrandLoading />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__dev/crash" element={<CrashProbe />} />
          )}
          <Route
            path="/pilot-provisioning"
            element={
              <ProtectedRoute allow={["admin"]}>
                <PilotProvisioning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-lifecycle"
            element={
              <ProtectedRoute allow={["admin"]}>
                <StudentDataLifecycle />
              </ProtectedRoute>
            }
          />
          <Route
            path="/backup-recovery"
            element={
              <ProtectedRoute allow={["admin"]}>
                <BackupRecovery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/curriculum"
            element={
              <ProtectedRoute>
                <CurriculumBrowser />
              </ProtectedRoute>
            }
          />
          <Route
            path="/curriculum-admin"
            element={
              <ProtectedRoute allow={["admin"]}>
                <CurriculumAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/authority-review"
            element={
              <ProtectedRoute allow={["admin"]}>
                <AuthorityReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge-admin"
            element={
              <ProtectedRoute allow={["admin"]}>
                <KnowledgeAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge-state"
            element={
              <ProtectedRoute>
                <KnowledgeState />
              </ProtectedRoute>
            }
          />
          <Route
            path="/diagnosis"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <Diagnosis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/diagnosis/referrals"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <ReferralInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recovery"
            element={
              <ProtectedRoute>
                <Recovery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn"
            element={
              <ProtectedRoute allow={["student"]}>
                <TeachingSession />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teaching"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <TeacherTeaching />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mistakes"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <MistakeIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mastery"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <MasteryTeacher />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-progress"
            element={
              <ProtectedRoute allow={["student"]}>
                <StudentProgress />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-journey"
            element={
              <ProtectedRoute allow={["student"]}>
                <StudentJourney />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-rechecks"
            element={
              <ProtectedRoute allow={["student"]}>
                <MyRechecks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-language"
            element={
              <ProtectedRoute allow={["student"]}>
                <MyLanguage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/language-evidence"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <LanguageEvidence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/language-evidence/review"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <AiProposalReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journey"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <TeacherJourney />
              </ProtectedRoute>
            }
          />
          <Route
            path="/classroom"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <ClassroomOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/case"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <TeacherCase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-case"
            element={
              <ProtectedRoute allow={["student"]}>
                <StudentCase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/command-center"
            element={
              <ProtectedRoute allow={["teacher", "admin"]}>
                <RecoveryCommandCenter />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
